import React, { useEffect, useState, useCallback, useRef } from 'react';
import { getProducts } from '../../services/productService';
import { useVirtualizer } from '@tanstack/react-virtual';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import ProductDetailsModal from '../../components/product/ProductDetailsModal';
import { useSelector } from 'react-redux'; 
import HoverPopover from '../../components/HoverPopove/HoverPopover';
// TODO: Update this import path to your actual selectUser selector path
import { selectUser } from '../../redux/features/auth/authSlice'; // Example path
import clsx from 'clsx';

import styles from './ProductsTable.module.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ProductsTable = () => {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [warehouses, setWarehouses] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterBySerial, setFilterBySerial] = useState('');
    const [filterByCategory, setFilterByCategory] = useState('');
    const [filterByWarehouse, setFilterByWarehouse] = useState('');
    const [filterByDepartment, setFilterByDepartment] = useState('');
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [rowHeight] = useState(40); 
    const navigate = useNavigate();
    const user = useSelector(selectUser); // Call useSelector at the top level
    const isUserAdmin = user?.permission === 'admin' || user?.permission === 'editor'; 
    
    const parentRef = useRef(null);

    const [popoverState, setPopoverState] = useState({
        product: null,
        position: null,
    });
    const popoverTimeoutRef = useRef(null);

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                await Promise.all([
                    fetchProducts(),
                    fetchCategories(),
                    fetchWarehouses(),
                    fetchDepartments()
                ]);
            } catch (error) {
                toast.error("Failed to fetch data");
            } finally {
                setIsLoading(false);
            }
        };
    
        fetchData();
        console.log(isUserAdmin);
    }, []);

    // Reset scroll position when filters change
    useEffect(() => {
        if (parentRef.current) {
            parentRef.current.scrollTop = 0;
        }
    }, [searchTerm, filterBySerial, filterByCategory, filterByWarehouse, filterByDepartment]);

        useEffect(() => {
        const parent = parentRef.current;
        if (!parent) return;

        const handleScroll = () => {
            if (popoverState.product) {
                setPopoverState({ product: null, position: null });
            }
        };

        parent.addEventListener('scroll', handleScroll, { passive: true });
        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            parent.removeEventListener('scroll', handleScroll);
            window.removeEventListener('scroll', handleScroll);
        };
    }, [popoverState.product]); // Only re-run if the popover is active


    // ... (keep fetch functions, handleUpdate, handleProductClick, etc.)

    // 👇 NEW EVENT HANDLERS FOR HOVER
const handleMouseOver = (e, product) => {
    clearTimeout(popoverTimeoutRef.current);
    if (popoverState.product?._id === product._id) {
        return;
    }
    
    const rect = e.currentTarget.getBoundingClientRect();
    setPopoverState({
        product: product,
        position: {
            top: rect.top,
            left: rect.left,
        },
    });
};

const handleMouseLeave = () => {
    popoverTimeoutRef.current = setTimeout(() => {
        setPopoverState({ product: null, position: null });
    }, 100); // A short delay is all we need
};




    const fetchProducts = async () => {
        try {
            const data = await getProducts();
            setProducts(data);
        } catch (error) {
            toast.error("Failed to fetch products");
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await axios.get(`${BACKEND_URL}/api/categories`);
            setCategories(response.data);
        } catch (error) {
            toast.error("Failed to fetch categories");
        }
    };

    const fetchWarehouses = async () => {
        try {
            const response = await axios.get(`${BACKEND_URL}/api/warehouses`);
            setWarehouses(response.data);
        } catch (error) {
            toast.error("Failed to fetch warehouses");
        }
    };

    const fetchDepartments = async () => {
        try {
            const response = await axios.get(`${BACKEND_URL}/api/departments`);
            setDepartments(response.data);
        } catch (error) {
            toast.error("Failed to fetch departments");
        }
    };

    const handleUpdate = (product) => {
        navigate('/update-product', { state: { product } });
    };

    const handleProductClick = (product) => {
        setSelectedProduct(product);
        setIsModalOpen(true);
    };

    const getCategoryName = (categoryId) => {
        const category = categories.find(cat => cat._id === categoryId);
        return category ? category.name : 'Unknown';
    };

    const getWarehouseName = (warehouseId) => {
        const warehouse = warehouses.find(wh => wh._id === warehouseId);
        return warehouse ? warehouse.name : 'Unknown';
    };

    const getDepartmentName = (departmentId) => {
        const department = departments.find(dep => dep._id === departmentId);
        return department ? department.name : 'Unknown';
    };

    const filteredProducts = products.filter(product => {
        return (
            product.productName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            product.productCode.toLowerCase().includes(searchTerm.toLowerCase())
        ) &&
        (filterBySerial === '' || product.managedBySerial.toString() === filterBySerial) &&
        (filterByCategory === '' || product.category === filterByCategory) &&
        (filterByWarehouse === '' || product.warehouse === filterByWarehouse) &&
        (filterByDepartment === '' || product.department === filterByDepartment)
    });

    const rowVirtualizer = useVirtualizer({
    count: filteredProducts.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => rowHeight,
    overscan: 5,
    });


    return (
        <div className={styles.container}>
         {/* 👇 RENDER THE PORTAL POPOVER HERE */}
            <HoverPopover
                product={popoverState.product}
                position={popoverState.position}
                onUpdate={handleUpdate}
                // THE FIX IS HERE: We re-use the *exact same* event handlers.
                onMouseOver={() => clearTimeout(popoverTimeoutRef.current)} // When entering the popover, cancel the hide timer.
                onMouseLeave={handleMouseLeave} // When leaving the popover, start the hide timer.
            />
            <h2 className={styles.title}>Warehouse</h2>
            {isLoading ? (
            <div className={styles.loadingContainer}>
                <div className={styles.spinner}></div>
                <p>Loading data...</p>
            </div>
        ) : (
            <>
            <div className={styles.filtersContainer}>
                <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>Search Products</label>
                    <input 
                        type="text" 
                        placeholder="Search by name or code..." 
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className={styles.filterInput}
                    />
                </div>
                <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>Serial Management</label>
                    <select 
                        value={filterBySerial} 
                        onChange={(e) => setFilterBySerial(e.target.value)}
                        className={styles.filterSelect}
                    >
                        <option value="">All</option>
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                    </select>
                </div>
                <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>Category</label>
                    <select 
                        value={filterByCategory} 
                        onChange={(e) => setFilterByCategory(e.target.value)}
                        className={styles.filterSelect}
                    >
                        <option value="">All Categories</option>
                        {categories.map(category => (
                            <option key={category._id} value={category._id}>
                                {category.name}
                            </option>
                        ))}
                    </select>
                </div>
                <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>Warehouse</label>
                    <select 
                        value={filterByWarehouse} 
                        onChange={(e) => setFilterByWarehouse(e.target.value)}
                        className={styles.filterSelect}
                    >
                        <option value="">All Warehouses</option>
                        {warehouses.map(warehouse => (
                            <option key={warehouse._id} value={warehouse._id}>
                                {warehouse.name}
                            </option>
                        ))}
                    </select>
                </div>

                <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>Department</label>
                    <select 
                        value={filterByDepartment} 
                        onChange={(e) => setFilterByDepartment(e.target.value)}
                        className={styles.filterSelect}
                    >
                        <option value="">All Departments</option>
                        {departments.map(department => (
                            <option key={department._id} value={department._id}>
                                {department.name}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            <div className={styles.tableWrapper}>
                        {/* Fixed Headers */}
                        <table className={styles.tableHeader}>
                            <thead>
                                <tr>
                                    <th className={styles.tableCell}>Product Code</th>
                                    <th className={styles.tableCell}>Product Name</th>
                                    <th className={styles.tableCell}>Total Quantity</th>
                                    <th className={styles.tableCell}>Available Quantity</th>
                                    <th className={styles.tableCell}>Category</th>
                                    <th className={styles.tableCell}>Warehouse</th>
                                    <th className={styles.tableCell}>Department</th>
                                    <th className={styles.tableCell}>Managed By Serial</th>
                                </tr>
                            </thead>
                        </table>

                        {/* Virtual Scroll Container */}
                        <div 
                            ref={parentRef} 
                            className={styles.virtualScrollContainer}
                        >
                            <div
                                style={{
                                    height:  `${rowVirtualizer.getTotalSize()}px`,
                                    width: '100%',
                                    position: 'relative'
                                }}
                            >
                                {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                                const product = filteredProducts[virtualRow.index];

                                return (
                                    <div
                                    key={virtualRow.key}                       // use the key the lib gives you
                                    className={clsx(
                                        styles.virtualRow,
                                        virtualRow.index % 2 === 0 && styles.evenRow /* custom striping */
                                    )}
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: `${rowHeight}px`,
                                        transform: `translateY(${virtualRow.start}px)`,
                                        willChange: 'transform',
                                    }}
                                    onClick={() => handleProductClick(product)}
                                    >
                                            <div className={styles.tableCell}>{product.productCode}</div>
                                            
                                            {/* ✨ Add the hover handlers ONLY to this specific cell ✨ */}
                                            <div
                                                className={`${styles.tableCell} ${styles.productNameCell}`}
                                                onMouseOver={(e) => handleMouseOver(e, product)}
                                                onMouseLeave={handleMouseLeave}
                                            >
                                                {product.productName}
                                            </div>

                                            <div className={styles.tableCell}>{product.quantity}</div>
                                            <div className={styles.tableCell}>{product.quantityAvailable}</div>
                                            <div className={styles.tableCell}>{getCategoryName(product.category)}</div>
                                            <div className={styles.tableCell}>{getWarehouseName(product.warehouse)}</div>
                                            <div className={styles.tableCell}>{getDepartmentName(product.department)}</div>
                                            <div className={styles.tableCell}>{product.managedBySerial ? 'Yes' : 'No'}</div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {isModalOpen && selectedProduct && (
                        <ProductDetailsModal 
                            productId={selectedProduct._id} 
                            onClose={() => setIsModalOpen(false)} 
                        />
                    )}
                </>
            )}
        </div>
    );
};

export default ProductsTable;